<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex items-center justify-between mb-2">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">API Tokens / Service Accounts</h1>
      <button @click="openCreate"
        class="px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors">
        + New Token
      </button>
    </div>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
      Machine identities for automated access. Tokens are JWT-signed, expire after 1 year by default,
      and can be revoked instantly without redeploying.
    </p>

    <!-- Filter -->
    <div class="flex gap-2 mb-4">
      <button v-for="s in ['all', 'active', 'revoked']" :key="s"
        @click="statusFilter = s; fetchAccounts()"
        class="px-3 py-1 text-xs font-medium rounded-full border transition-colors"
        :class="statusFilter === s
          ? 'bg-blue-600 text-white border-blue-600'
          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'">
        {{ s.charAt(0).toUpperCase() + s.slice(1) }}
      </button>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-16">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500"></div>
    </div>

    <div v-else-if="accounts.length === 0" class="text-center py-16 text-gray-400 dark:text-gray-500 text-sm">
      No service accounts yet.
    </div>

    <div v-else class="space-y-3">
      <div v-for="sa in accounts" :key="sa.id"
        class="card px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div class="flex items-start gap-3 min-w-0 flex-1">
          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 mt-0.5"
            :class="statusClass(sa.status)">{{ sa.status }}</span>
          <div class="min-w-0">
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ sa.name }}</p>
            <p v-if="sa.description" class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ sa.description }}</p>
            <div class="flex flex-wrap gap-2 mt-1">
              <span class="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">
                {{ sa.role }}
              </span>
              <span class="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded font-mono">
                {{ sa.token_hint }}
              </span>
              <span class="text-xs text-gray-400 dark:text-gray-500">
                Expires {{ formatDate(sa.expires_at) }}
              </span>
              <span v-if="sa.last_used_at" class="text-xs text-gray-400 dark:text-gray-500">
                Last used {{ formatDate(sa.last_used_at) }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0">
          <button v-if="sa.status === 'active'"
            @click="rotate(sa)"
            :disabled="rotatingId === sa.id"
            title="Issue a new JWT and invalidate all previous tokens for this service account"
            class="px-3 py-1.5 text-xs font-medium rounded-md border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors">
            {{ rotatingId === sa.id ? 'Rotating…' : 'Rotate' }}
          </button>
          <button v-if="sa.status === 'active'"
            @click="confirmRevoke(sa)"
            class="px-3 py-1.5 text-xs font-medium rounded-md border border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors">
            Revoke
          </button>
          <button @click="confirmDelete(sa)"
            class="px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            Delete
          </button>
        </div>
      </div>
    </div>

    <!-- Create modal -->
    <div v-if="showCreate" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">New Service Account</h2>
          <button @click="closeCreate" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="px-6 py-5 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name <span class="text-red-500">*</span></label>
            <input v-model="form.name" type="text" placeholder="e.g. CI/CD Pipeline"
              class="input w-full" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <input v-model="form.description" type="text" placeholder="Optional description"
              class="input w-full" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role <span class="text-red-500">*</span></label>
            <select v-model="form.role" class="input w-full">
              <option value="view">view — read-only access</option>
              <option value="edit">edit — create &amp; update content</option>
              <option value="admin">admin — full tenant access</option>
              <option value="uploader">uploader — list products + upload SBOMs/scan reports only</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expiry <span class="text-xs text-gray-400">(defaults to 1 year)</span>
            </label>
            <input v-model="form.expires_at" type="date" class="input w-full" />
          </div>
          <p v-if="createError" class="text-sm text-red-600 dark:text-red-400">{{ createError }}</p>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button @click="closeCreate"
            class="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button @click="createAccount" :disabled="creating"
            class="px-4 py-2 text-sm rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {{ creating ? 'Creating…' : 'Create & Show Token' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Token reveal modal (shown once after creation) -->
    <div v-if="newToken" class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Token Created</h2>
        </div>
        <div class="px-6 py-5 space-y-4">
          <div class="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 px-4 py-3">
            <p class="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
              Copy this token now — it will not be shown again.
            </p>
            <p class="text-xs text-yellow-700 dark:text-yellow-400">
              Store it securely (e.g. a secrets manager or CI variable). Revoke it immediately if it is compromised.
            </p>
          </div>
          <div class="relative">
            <pre class="bg-gray-900 text-green-400 text-xs rounded-lg px-4 py-3 overflow-x-auto break-all whitespace-pre-wrap select-all font-mono">{{ newToken }}</pre>
            <button @click="copyToken"
              class="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded transition-colors">
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button @click="dismissToken"
            class="px-4 py-2 text-sm rounded-md text-white bg-blue-600 hover:bg-blue-700">
            I've saved it, close
          </button>
        </div>
      </div>
    </div>

    <!-- Revoke confirmation -->
    <DeleteConfirmationModal
      :show="!!revokeTarget"
      title="Revoke Service Account"
      :message="revokeTarget ? `Revoke '${revokeTarget.name}'? Any JWT issued for this account will be rejected immediately. This cannot be undone.` : ''"
      confirm-text="Revoke"
      @confirm="executeRevoke"
      @cancel="revokeTarget = null"
    />

    <!-- Delete confirmation -->
    <DeleteConfirmationModal
      :show="!!deleteTarget"
      title="Delete Service Account"
      :message="deleteTarget ? `Permanently delete '${deleteTarget.name}'? This cannot be undone.` : ''"
      confirm-text="Delete"
      @confirm="executeDelete"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue'

const store = useStore()

// Matches the helper used in store/index.js and other views.
function getApiBase() {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const clean = envUrl.replace(/\/$/, '')
  return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`
}
const API = getApiBase()

const accounts = ref([])
const loading = ref(false)
const statusFilter = ref('all')

const showCreate = ref(false)
const creating = ref(false)
const createError = ref('')
const form = ref({ name: '', description: '', role: 'view', expires_at: '' })

const newToken = ref('')
const copied = ref(false)

const revokeTarget = ref(null)
const deleteTarget = ref(null)
const rotatingId = ref(null)

const tenantId = store.getters.currentTenantId
const headers = () => ({ Authorization: `Bearer ${store.state.token}` })

async function fetchAccounts() {
  loading.value = true
  try {
    const params = statusFilter.value !== 'all' ? { status: statusFilter.value } : {}
    const { data } = await axios.get(
      `${API}/admin/tenants/${tenantId}/service-accounts`,
      { headers: headers(), params }
    )
    accounts.value = data.data ?? data
  } catch (e) {
    console.error('Failed to fetch service accounts', e)
  } finally {
    loading.value = false
  }
}

function openCreate() {
  form.value = { name: '', description: '', role: 'view', expires_at: '' }
  createError.value = ''
  showCreate.value = true
}
function closeCreate() { showCreate.value = false }

async function createAccount() {
  if (!form.value.name || !form.value.role) {
    createError.value = 'Name and role are required.'
    return
  }
  creating.value = true
  createError.value = ''
  try {
    const payload = {
      name: form.value.name,
      description: form.value.description,
      role: form.value.role,
    }
    if (form.value.expires_at) payload.expires_at = new Date(form.value.expires_at).toISOString()
    const { data } = await axios.post(
      `${API}/admin/tenants/${tenantId}/service-accounts`,
      payload,
      { headers: headers() }
    )
    newToken.value = data.token
    accounts.value.unshift(data.service_account)
    closeCreate()
  } catch (e) {
    createError.value = e.response?.data?.error ?? 'Failed to create service account'
  } finally {
    creating.value = false
  }
}

async function copyToken() {
  try {
    await navigator.clipboard.writeText(newToken.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (e) { /* clipboard not available */ }
}

function dismissToken() {
  newToken.value = ''
  copied.value = false
}

function confirmRevoke(sa) { revokeTarget.value = sa }
function confirmDelete(sa) { deleteTarget.value = sa }

async function rotate(sa) {
  if (rotatingId.value) return
  rotatingId.value = sa.id
  try {
    const { data } = await axios.post(
      `${API}/admin/tenants/${tenantId}/service-accounts/${sa.id}/rotate`,
      {},
      { headers: headers() }
    )
    newToken.value = data.token
    // Update in place: hint changes, identity stays
    const existing = accounts.value.find(a => a.id === sa.id)
    if (existing && data.service_account) {
      existing.token_hint = data.service_account.token_hint
    }
  } catch (e) {
    console.error('Rotate failed', e)
  } finally {
    rotatingId.value = null
  }
}

async function executeRevoke() {
  try {
    await axios.post(
      `${API}/admin/tenants/${tenantId}/service-accounts/${revokeTarget.value.id}/revoke`,
      {},
      { headers: headers() }
    )
    const sa = accounts.value.find(a => a.id === revokeTarget.value.id)
    if (sa) sa.status = 'revoked'
  } catch (e) {
    console.error('Revoke failed', e)
  } finally {
    revokeTarget.value = null
  }
}

async function executeDelete() {
  try {
    await axios.delete(
      `${API}/admin/tenants/${tenantId}/service-accounts/${deleteTarget.value.id}`,
      { headers: headers() }
    )
    accounts.value = accounts.value.filter(a => a.id !== deleteTarget.value.id)
  } catch (e) {
    console.error('Delete failed', e)
  } finally {
    deleteTarget.value = null
  }
}

function statusClass(status) {
  return {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    revoked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  }[status] ?? 'bg-gray-100 text-gray-600'
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

onMounted(fetchAccounts)
</script>
